/**
 * Light/dark theme.
 *
 * The theme is an attribute on <html>; `tokens.css` redefines its variables under
 * `[data-theme='light']`. Nothing else in the app needs to know which theme is active.
 *
 * Dark is the brand default (§6.2), so a first-time visitor sees the product as designed.
 * An explicit choice is remembered per browser.
 */

export type Theme = 'dark' | 'light';

export const DEFAULT_THEME: Theme = 'dark';
export const THEME_STORAGE_KEY = 'guardian-ai.theme';

export function isTheme(value: unknown): value is Theme {
  return value === 'dark' || value === 'light';
}

/**
 * Storage access throws in some privacy modes, and this is only a display preference, so
 * every failure falls back to the default rather than breaking the page.
 */
export function readStoredTheme(): Theme | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function storeTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Preference is not persisted this session. The page still renders correctly.
  }
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
}

/** The theme currently on <html>, which the boot script in index.html sets before first paint. */
export function currentTheme(): Theme {
  const applied = document.documentElement.dataset.theme;
  return isTheme(applied) ? applied : DEFAULT_THEME;
}
